// React
import { useEffect, useState } from "react";
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom"
import React from "react"

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"

// MUI Components
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import Breadcrumbs from "@mui/material/Breadcrumbs"
import Link from "@mui/material/Link"

// MUI Icons
import HomeIcon from "@mui/icons-material/Home"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import EditIcon from "@mui/icons-material/Edit"
import MenuIcon from "@mui/icons-material/Menu"

// Utils
import { ToastContainer, toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import jwtDecode from "jwt-decode"
import Swal from "sweetalert2"

// Styles
import { green } from "@mui/material/colors"

// Types
import { TokenPayload } from "../../../@types"

// Services
import {
  getAllMenus,
  updateMenu,
  getMenuByID,
} from "../../../services/MenuService"
// import CreateMenu from "./CreateMenu";

interface FormData {
  menu_id: string
  menu_name: string
  menu_name_th: string
  menu_name_en: string
  is_active: boolean
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  editId?: string
}

const ManageMenu: React.FC<MyEditorProps> = ({ type = "add", editId }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const location = useLocation()

  const userRoleType = useAppSelector((state) => state.user.roletype)
  const menuDataFromState = location.state?.menuData
  const allowedRoles = ["Super Admin", "Support"]
  const havePermission = allowedRoles.includes(userRoleType)

  const [formData, setFormData] = useState<FormData>({
    menu_id: "",
    menu_name: "",
    menu_name_en: "",
    menu_name_th: "",
    is_active: true,
  })
  const [dataLoading, setDataLoading] = useState<boolean>(isEdit)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  if (!havePermission) {
    navigate("/forbidden", { replace: true })
  }

  useEffect(() => {
    if (!isEdit || !id) return
    if (type === "edit") {
      if (menuDataFromState) {
        setFormData(menuDataFromState)
        setDataLoading(false)
      }
    }
  }, [isEdit, id, menuDataFromState])

  const formValidationSchema = Yup.object().shape({
    ...(isEdit
      ? {}
      : { menu_id: Yup.string().required(`${t("Menu ID is required")}`) }),
    menu_name: Yup.string().required(`${t("Menu Name is required")}`),
    menu_name_th: Yup.string().required(`${t("Menu Name(TH) is required")}`),
    menu_name_en: Yup.string().required(`${t("Menu Name(EN) is required")}`),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(formValidationSchema) })

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, is_active: event.target.checked }))
  }

  const onSubmit = (values: any) => {
    const submit = async () => {
      try {
        const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
        setSubmitLoading(true)
        let response
        if (isEdit) {
          response = await updateMenu({
            id: id,
            menu_id: formData.menu_id,
            menu_name: formData.menu_name,
            menu_name_th: formData.menu_name_th,
            menu_name_en: formData.menu_name_en,
            is_active: formData.is_active,
            modified_by: parsedToken.Username,
          })
        }
        if (response) {
          setSubmitLoading(false)
          const responseData = response.data
          if (!responseData?.success) {
            toast.error(
              i18n.language === "th"
                ? responseData.message_th
                : responseData.message_en,
              { position: "top-center", autoClose: 3000, theme: "light" }
            )
          } else {
            Swal.fire({
              title:
                i18n.language === "th"
                  ? responseData.message_th
                  : responseData.message_en,
              icon: "success",
              confirmButtonColor: "#43A047",
              confirmButtonText: "OK!",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.isConfirmed)
                navigate("/admin/masterdata/menus", { replace: true })
            })
          }
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      }
    }
    submit()
  }

  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <ToastContainer style={{ width: "400px" }} />
      {havePermission && (
        <>
          <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
            <Box display="flex" gap={3} sx={{ mb: 3 }}>
              <Box
                display="flex"
                component={ReactRouterLink}
                onClick={() => window.history.back()}
                sx={{
                  textDecoration: "none",
                  color: "#0d47a1",
                  alignItems: "center",
                  ":hover": { color: "#1976d2" },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
                <Typography sx={{ fontWeight: "bold" }}>{t("Back")}</Typography>
              </Box>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin"
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Dashboard")}
                </Link>
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin/masterdata/menus"
                >
                  <MenuIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Menu")}
                </Link>
                <Typography
                  sx={{ display: "flex", alignItems: "center" }}
                  color="text.primary"
                >
                  {isEdit ? (
                    <>
                      <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                      {t("menu.title_edit_menu")}
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon
                        sx={{ mr: 0.5 }}
                        fontSize="inherit"
                      />
                      {t("Create New Menu")}
                    </>
                  )}
                </Typography>
              </Breadcrumbs>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "70px",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", textAlign: "left" }}
            >
              {isEdit ? t("menu.title_edit_menu") : t("Create New Menu")}
            </Typography>
          </Box>

          <Box component="form" noValidate sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="menu_id"
                  label={t("Menu ID")}
                  disabled={isEdit}
                  {...(!isEdit ? register("menu_id") : {})}
                  error={!!(errors as any).menu_id}
                  value={isEdit ? formData.menu_id : undefined}
                  onChange={
                    isEdit
                      ? undefined
                      : (event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormData((prev) => ({
                            ...prev,
                            menu_id: event.target.value,
                          }))
                          ;(errors as any).menu_id = undefined
                        }
                  }
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).menu_id?.message}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="menu_name"
                  label={t("Menu Name")}
                  disabled={isEdit}
                  {...register("menu_name")}
                  error={!!(errors as any).menu_name}
                  value={formData.menu_name}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      menu_name: event.target.value,
                    }))
                    ;(errors as any).menu_name = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).menu_name?.message}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="menu_name_en"
                  label={t("Menu Name(EN)")}
                  {...register("menu_name_en")}
                  error={!!(errors as any).menu_name_en}
                  value={formData.menu_name_en}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      menu_name_en: event.target.value,
                    }))
                    ;(errors as any).menu_name_en = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).menu_name_en?.message}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="menu_name_th"
                  label={t("Menu Name(TH)")}
                  {...register("menu_name_th")}
                  error={!!(errors as any).menu_name_th}
                  value={formData.menu_name_th}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      menu_name_th: event.target.value,
                    }))
                    ;(errors as any).menu_name_th = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {(errors as any).menu_name_th?.message}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={t("Active")}
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={handleActiveChange}
                      inputProps={{ "aria-label": "Active" }}
                    />
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  sx={{ mt: 5, mb: 2, color: "white" }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={submitLoading}
                >
                  {isEdit ? t("Save") : t("Create")}
                  {submitLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                        zIndex: 999,
                      }}
                    />
                  )}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 5, mb: 2 }}
                  component={ReactRouterLink}
                  to="/admin/masterdata/menus"
                >
                  {t("Cancel")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
}

export default ManageMenu;
